import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../api/api-client-context';
import { ADMIN_AUTH_API_CLIENT_NAME } from '../constants';
import { createFeatureFlagsApi } from './feature-flags-api';
import { featureFlagKeys } from './feature-flags-keys';

/**
 * Fetches the list of active feature flag keys for the current user from the backend.
 * Uses staleTime + gcTime: Infinity so the list is fetched once per session and cached
 * until a manual refetch is triggered.
 *
 * Pass `enabled: false` (e.g. `enabled: isAuthenticated`) to avoid firing an
 * unauthenticated request before auth is established. Defaults to `true` so existing
 * callers that pass nothing keep the same behaviour.
 *
 * Returns the raw TanStack Query result — callers can access `data`, `isLoading`,
 * `error`, and `refetch`.
 */
export function useFeatureFlags(enabled?: boolean): UseQueryResult<string[]> {
  const client = useApiClient(ADMIN_AUTH_API_CLIENT_NAME);
  const api = createFeatureFlagsApi(client);

  return useQuery({
    enabled: enabled ?? true,
    gcTime: Infinity,
    queryFn: api.fetchActiveFlags,
    queryKey: featureFlagKeys.active(),
    staleTime: Infinity,
  });
}
